/**
 * bot-status の健全性判定。
 *
 * **表示より先にここを固定する。** 監視の価値は「異常を異常と言えるか」に集約されるので、
 * 判定の境界が UI のレンダリングに埋もれると回帰に気付けない。純関数に切り出してテストで縛る。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business bot 稼働レポートの鮮度・健全性を判定し、表示に依らない形で異常を確定する。
 * @graph-connects none
 */
import type { BotServiceStatus, BotStatusRecord } from '@cortex/domain-admin-api';

/** 健全性の段階。 @graph-connects none */
export type HealthLevel = 'ok' | 'warn' | 'down' | 'unknown';

/**
 * 経過時間の表示単位と値。文言は表示層（`bot-status-messages.ts`）が言語に応じて組み立てる。
 * @graph-connects none
 */
export interface Duration {
  unit: 'minutes' | 'hours' | 'days';
  count: number;
}

/**
 * 健全性判定の理由。文言ではなく種別 + 数値で持ち、表示層が翻訳する。
 *
 * 判定ロジックを言語から切り離すのが目的。理由を文字列で返すと、翻訳を足すたびに
 * この純関数と境界テストを触ることになる。
 * @graph-connects none
 */
export type HealthReason =
  | { kind: 'noReport' }
  | { kind: 'invalidTime' }
  | { kind: 'stale'; ageMs: number }
  | { kind: 'fresh' }
  | { kind: 'stopped'; detail?: string }
  | { kind: 'failures'; count: number; window?: string }
  | { kind: 'idle'; idleMs: number }
  | { kind: 'healthy' }
  | { kind: 'servicesDown'; count: number }
  | { kind: 'warnings'; count: number }
  | { kind: 'allHealthy' };

/** ホスト状態の警告。表示層が翻訳する。 @graph-connects none */
export type HostWarning =
  | { kind: 'backupUnmeasured'; source: string | null }
  | { kind: 'backupStale'; hours: number }
  | { kind: 'swapHigh'; percent: number }
  | { kind: 'memoryLow'; percent: number }
  | { kind: 'diskLow'; gb: string };

/**
 * レポートが古いとみなす閾値（ミリ秒）。
 *
 * collector は 5 分ごとに送る。1 回の取りこぼし（ネットワーク瞬断・Cloud Run のコールドスタート）で
 * 赤くすると誤報だらけになるので、3 回ぶん外したら異常とみなす。
 *
 * @graph-connects none
 */
export const STALE_THRESHOLD_MS = 15 * 60 * 1000;

/**
 * バックアップが古いとみなす閾値（時間）。`check-backup-freshness` の既定と揃える。
 * @graph-connects none
 */
export const BACKUP_STALE_HOURS = 24;

/**
 * bot のログが停滞しているとみなす閾値（ミリ秒）。
 *
 * **プロセスが生きていても仕事をしていない状態を捕まえるのがこの判定の目的。**
 * ただし深夜など単に PR が来ない時間帯は正常に無音なので、長めに取る。
 *
 * @graph-connects none
 */
export const IDLE_THRESHOLD_MS = 6 * 60 * 60 * 1000;

/** レポート全体の鮮度。 @graph-connects none */
export interface ReportFreshness {
  level: HealthLevel;
  /** 受理からの経過（ミリ秒）。未受信なら null。 */
  ageMs: number | null;
  reason: HealthReason;
}

/**
 * レポートの鮮度を判定する。
 *
 * **collector が死んだことを検知するのがこの関数の唯一の目的。** 中身が全て正常でも、
 * レポート自体が古ければその内容は信用できない。「測れていない」を「正常」に倒さない。
 *
 * @param record 最新レポート（未受信なら null）
 * @param serverTime サーバーが返した基準時刻（ISO8601）
 * @returns 鮮度の判定結果
 * @graph-connects none
 */
export function evaluateFreshness(
  record: BotStatusRecord | null,
  serverTime: string,
): ReportFreshness {
  if (!record) {
    return { level: 'unknown', ageMs: null, reason: { kind: 'noReport' } };
  }
  const ageMs = new Date(serverTime).getTime() - new Date(record.storedAt).getTime();
  if (!Number.isFinite(ageMs)) {
    return { level: 'unknown', ageMs: null, reason: { kind: 'invalidTime' } };
  }
  if (ageMs > STALE_THRESHOLD_MS) {
    return { level: 'down', ageMs, reason: { kind: 'stale', ageMs } };
  }
  return { level: 'ok', ageMs, reason: { kind: 'fresh' } };
}

/**
 * サービス 1 件の健全性を判定する。
 *
 * `running` だけを見ない。`service-review` は engine 起動の 87% が SIGKILL で失敗しながら
 * プロセスも SSE 接続も正常だった。失敗が出ているなら「動いている」とは言わない。
 *
 * @param service 判定対象
 * @param serverTime 基準時刻（ISO8601）
 * @returns 健全性と理由
 * @graph-connects none
 */
export function evaluateService(
  service: BotServiceStatus,
  serverTime: string,
): { level: HealthLevel; reason: HealthReason } {
  if (!service.running) {
    return { level: 'down', reason: { kind: 'stopped', detail: service.detail } };
  }
  if ((service.recentFailures ?? 0) > 0) {
    return {
      level: 'warn',
      reason: {
        kind: 'failures',
        count: service.recentFailures ?? 0,
        window: service.failureWindow,
      },
    };
  }
  // launchd の定期ジョブは実行間隔が長く、ログ更新も伴わないので停滞判定の対象にしない。
  if (service.kind === 'tmux' && service.lastActivityAt) {
    const idleMs = new Date(serverTime).getTime() - new Date(service.lastActivityAt).getTime();
    if (Number.isFinite(idleMs) && idleMs > IDLE_THRESHOLD_MS) {
      return { level: 'warn', reason: { kind: 'idle', idleMs } };
    }
  }
  return { level: 'ok', reason: { kind: 'healthy' } };
}

/**
 * ホスト状態の警告を並べる。問題が無ければ空配列。
 *
 * バックアップは **測れないこと自体を警告にする**。`null` を「問題なし」として扱うと、
 * 外付けディスクが認識されていないのに正常に見えるという実際に起きた失敗を繰り返す。
 *
 * @param record 最新レポート
 * @returns 警告の配列
 * @graph-connects none
 */
export function collectHostWarnings(record: BotStatusRecord): HostWarning[] {
  const warnings: HostWarning[] = [];
  const metrics = record.hostMetrics;
  if (!metrics) return warnings;

  if (metrics.backupAgeHours === null || metrics.backupAgeHours === undefined) {
    warnings.push({ kind: 'backupUnmeasured', source: metrics.backupSource ?? null });
  } else if (metrics.backupAgeHours >= BACKUP_STALE_HOURS) {
    warnings.push({ kind: 'backupStale', hours: metrics.backupAgeHours });
  }

  // swap を使い切ると jetsam が走り、bot の claude が SIGKILL される。実際にそれで
  // レビューが数日止まったので、埋まりきる前に見えるようにする。
  if (metrics.swapTotalBytes && metrics.swapUsedBytes) {
    const ratio = metrics.swapUsedBytes / metrics.swapTotalBytes;
    if (ratio >= 0.9) {
      warnings.push({ kind: 'swapHigh', percent: Math.round(ratio * 100) });
    }
  }

  if (metrics.memoryTotalBytes && metrics.memoryFreeBytes !== undefined) {
    const freeRatio = metrics.memoryFreeBytes / metrics.memoryTotalBytes;
    if (freeRatio < 0.1) {
      warnings.push({ kind: 'memoryLow', percent: Math.round(freeRatio * 100) });
    }
  }

  if (metrics.diskFreeBytes !== undefined && metrics.diskFreeBytes < 20 * 1024 ** 3) {
    warnings.push({ kind: 'diskLow', gb: (metrics.diskFreeBytes / 1024 ** 3).toFixed(1) });
  }

  return warnings;
}

/**
 * 全体の健全性を 1 つに畳む。
 *
 * **レポートが古ければ中身を見ずに down。** 古いレポートの「全て正常」は、正常である証拠に
 * ならない。ここで中身を優先すると、collector が死んだ瞬間にページが緑で固定される。
 *
 * @param record 最新レポート
 * @param serverTime 基準時刻（ISO8601）
 * @returns 全体の健全性
 * @graph-connects none
 */
export function evaluateOverall(
  record: BotStatusRecord | null,
  serverTime: string,
): { level: HealthLevel; reason: HealthReason } {
  const freshness = evaluateFreshness(record, serverTime);
  if (freshness.level !== 'ok' || !record) {
    return { level: freshness.level, reason: freshness.reason };
  }

  const evaluated = record.services.map((service) => evaluateService(service, serverTime));
  const down = evaluated.filter((item) => item.level === 'down');
  if (down.length > 0) {
    return { level: 'down', reason: { kind: 'servicesDown', count: down.length } };
  }

  const warn = evaluated.filter((item) => item.level === 'warn');
  const hostWarnings = collectHostWarnings(record);
  const warnCount = warn.length + hostWarnings.length;
  if (warnCount > 0) {
    return { level: 'warn', reason: { kind: 'warnings', count: warnCount } };
  }

  return { level: 'ok', reason: { kind: 'allHealthy' } };
}

/**
 * 経過時間を表示単位に丸める。負値は 0 分に倒す（時刻ずれで未来になった場合）。
 * @param ms 経過ミリ秒
 * @returns 分 / 時間 / 日 のいずれかと整数値
 * @graph-connects none
 */
export function toDuration(ms: number): Duration {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return { unit: 'minutes', count: Math.max(minutes, 0) };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { unit: 'hours', count: hours };
  return { unit: 'days', count: Math.floor(hours / 24) };
}
